import React, { useState, useEffect, useCallback } from 'react';
import {
    Stack,
    Text,
    TextField,
    PrimaryButton,
    DefaultButton,
    IconButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    IStackTokens,
    mergeStyleSets,
    FontWeights,
    Dropdown,
    IDropdownOption,
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    IColumn,
    CommandBar,
    ICommandBarItemProps,
    Label,
    Separator,
    Dialog,
    DialogType,
    DialogFooter
} from '@fluentui/react';
import {
    FlowRegular,
    AddCircleRegular,
    DeleteRegular,
    SaveRegular,
    CheckmarkCircleRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';

// Types
interface ColumnInfo {
    name: string;
    data_type: string;
}

interface TableReference {
    id: string;
    table_name: string;
    schema_name?: string;
    alias?: string;
    columns: ColumnInfo[];
}

interface TableRelationship {
    id: string;
    source_table_id: string;
    source_column: string;
    target_table_id: string;
    target_column: string;
    relationship_type: string;
    join_type: string;
    relationship_name: string;
    relationship_name_he?: string;
    is_approved: boolean;
    auto_detected: boolean;
    confidence: number;
}

interface Props {
    sourceId: string;
    onSave?: () => void;
}

const stackTokens: IStackTokens = { childrenGap: 12 };

const relationshipTypeOptions: IDropdownOption[] = [
    { key: 'one_to_one', text: 'One to One' },
    { key: 'one_to_many', text: 'One to Many' },
    { key: 'many_to_one', text: 'Many to One' },
    { key: 'many_to_many', text: 'Many to Many' },
    { key: 'lookup', text: 'Lookup' }
];

const joinTypeOptions: IDropdownOption[] = [
    { key: 'inner', text: 'INNER JOIN' },
    { key: 'left', text: 'LEFT JOIN' },
    { key: 'right', text: 'RIGHT JOIN' },
    { key: 'full', text: 'FULL JOIN' }
];

const styles = mergeStyleSets({
    container: {
        padding: '16px'
    },
    header: {
        marginBottom: '16px'
    },
    relationshipCard: {
        padding: '16px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        marginBottom: '12px',
        backgroundColor: '#fafafa'
    },
    relationshipCardApproved: {
        borderColor: '#107c10'
    },
    flowIndicator: {
        fontSize: '24px',
        color: '#0078d4',
        margin: '0 12px'
    },
    tableBox: {
        padding: '8px 12px',
        border: '1px solid #c8c8c8',
        borderRadius: '4px',
        backgroundColor: '#fff',
        minWidth: '150px'
    },
    tableName: {
        fontWeight: FontWeights.semibold
    },
    columnName: {
        fontSize: '12px',
        color: '#666'
    },
    emptyState: {
        textAlign: 'center' as const,
        padding: '40px',
        backgroundColor: '#f3f3f3',
        borderRadius: '8px'
    }
});

export const RelationshipsEditor: React.FC<Props> = ({ sourceId, onSave }) => {
    const [tables, setTables] = useState<TableReference[]>([]);
    const [relationships, setRelationships] = useState<TableRelationship[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    
    // Cache for fetched columns per table
    const [tableColumnsCache, setTableColumnsCache] = useState<Record<string, ColumnInfo[]>>({});
    
    // Dialog state for adding new relationship
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [newRelationship, setNewRelationship] = useState<Partial<TableRelationship>>({
        relationship_type: 'many_to_one',
        join_type: 'left',
        is_approved: false,
        auto_detected: false,
        confidence: 1.0
    });
    
    // Fetch source data including tables and relationships
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Fetch source config
            const sourceResponse = await authenticatedFetch(
                `/api/admin/structured-sources/${sourceId}`
            );
            
            if (!sourceResponse.ok) {
                throw new Error('Failed to fetch source data');
            }
            
            const sourceData = await sourceResponse.json();
            
            // Also fetch tables from /tables endpoint (discovers active tables/views)
            const tablesResponse = await authenticatedFetch(
                `/api/admin/structured-sources/${sourceId}/tables`
            );
            
            let discoveredTables: TableReference[] = [];
            if (tablesResponse.ok) {
                const tablesData = await tablesResponse.json();
                discoveredTables = (tablesData.tables || [])
                    .filter((t: any) => t.active_for_queries) // Only include active tables
                    .map((t: any) => ({
                        id: t.name, // Use name as ID for discovered tables
                        table_name: t.name,
                        schema_name: t.schema,
                        columns: [] // Will be fetched on demand
                    }));
            }
            
            // Prefer discovered tables, fall back to stored config tables
            const tables = discoveredTables.length > 0 
                ? discoveredTables 
                : (sourceData.tables || sourceData.table_references || []);
            
            setTables(tables);
            
            // Extract relationships (backend returns "relationships" not "table_relationships")
            setRelationships(sourceData.relationships || sourceData.table_relationships || []);
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [sourceId]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Get table name by ID
    const getTableById = (tableId: string): TableReference | undefined => {
        return tables.find(t => t.id === tableId);
    };
    
    // Get table display name
    const getTableDisplayName = (tableId: string): string => {
        const table = getTableById(tableId);
        if (!table) return tableId;
        return table.schema_name 
            ? `${table.schema_name}.${table.table_name}`
            : table.table_name;
    };
    
    // Fetch columns for a table (with caching)
    const fetchTableColumns = useCallback(async (tableId: string) => {
        // Check cache first
        if (tableColumnsCache[tableId]) return;
        
        const table = tables.find(t => t.id === tableId);
        if (!table) return;
        
        try {
            const schemaParam = table.schema_name ? `?schema=${encodeURIComponent(table.schema_name)}` : '';
            const response = await authenticatedFetch(
                `/api/admin/structured-sources/${sourceId}/schema/${encodeURIComponent(table.table_name)}${schemaParam}`
            );
            
            if (response.ok) {
                const data = await response.json();
                setTableColumnsCache(prev => ({
                    ...prev,
                    [tableId]: data.columns || []
                }));
            }
        } catch (err) {
            console.error('Error fetching columns for table:', tableId, err);
        }
    }, [sourceId, tables, tableColumnsCache]);
    
    // Get columns for a table (from cache or original)
    const getTableColumns = (tableId: string): IDropdownOption[] => {
        // Check cache first
        const cachedColumns = tableColumnsCache[tableId];
        if (cachedColumns && cachedColumns.length > 0) {
            return cachedColumns.map(col => ({
                key: col.name,
                text: `${col.name} (${col.data_type})`
            }));
        }
        
        // Fall back to table's embedded columns
        const table = getTableById(tableId);
        if (!table || !table.columns) return [];
        return table.columns.map(col => ({
            key: col.name,
            text: `${col.name} (${col.data_type})`
        }));
    };
    
    // Table dropdown options
    const tableOptions: IDropdownOption[] = tables.map(t => ({
        key: t.id,
        text: t.schema_name ? `${t.schema_name}.${t.table_name}` : t.table_name
    }));
    
    // Add new relationship
    const handleAddRelationship = () => {
        if (!newRelationship.source_table_id || 
            !newRelationship.source_column ||
            !newRelationship.target_table_id ||
            !newRelationship.target_column) {
            setError('Please fill in all required fields');
            return;
        }
        
        const relationship: TableRelationship = {
            id: `rel-${Date.now()}`,
            source_table_id: newRelationship.source_table_id!,
            source_column: newRelationship.source_column!,
            target_table_id: newRelationship.target_table_id!,
            target_column: newRelationship.target_column!,
            relationship_type: newRelationship.relationship_type || 'many_to_one',
            join_type: newRelationship.join_type || 'left',
            relationship_name: newRelationship.relationship_name || '',
            is_approved: false,
            auto_detected: false,
            confidence: 1.0
        };
        
        setRelationships([...relationships, relationship]);
        setHasChanges(true);
        setShowAddDialog(false);
        setNewRelationship({
            relationship_type: 'many_to_one',
            join_type: 'left',
            is_approved: false,
            auto_detected: false,
            confidence: 1.0
        });
    };
    
    // Delete relationship
    const handleDeleteRelationship = (id: string) => {
        setRelationships(relationships.filter(r => r.id !== id));
        setHasChanges(true);
    };
    
    // Toggle approval
    const handleToggleApproval = (id: string) => {
        setRelationships(relationships.map(r => 
            r.id === id ? { ...r, is_approved: !r.is_approved } : r
        ));
        setHasChanges(true);
    };
    
    // Update relationship field
    const updateRelationship = (id: string, field: keyof TableRelationship, value: any) => {
        setRelationships(relationships.map(r =>
            r.id === id ? { ...r, [field]: value } : r
        ));
        setHasChanges(true);
    };
    
    // Save relationships
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        
        try {
            const response = await authenticatedFetch(
                `/api/admin/structured-sources/${sourceId}/relationships`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ relationships })
                }
            );
            
            if (!response.ok) {
                throw new Error('Failed to save relationships');
            }
            
            setSuccess('Relationships saved successfully');
            setHasChanges(false);
            
            // Don't call onSave - it causes parent to refetch and close/reset the panel
            // if (onSave) {
            //     onSave();
            // }
            
            setTimeout(() => setSuccess(null), 3000);
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };
    
    // Command bar items
    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'add',
            text: 'Add Relationship',
            iconProps: { iconName: 'Add' },
            disabled: tables.length < 2,
            onClick: () => setShowAddDialog(true)
        },
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            onClick: () => { fetchData(); }
        }
    ];
    
    const commandBarFarItems: ICommandBarItemProps[] = [
        {
            key: 'save',
            text: 'Save All',
            iconProps: { iconName: 'Save' },
            disabled: !hasChanges || saving,
            onClick: () => { handleSave(); }
        }
    ];
    
    if (loading) {
        return (
            <Stack horizontalAlign="center" styles={{ root: { padding: '40px' } }}>
                <Spinner size={SpinnerSize.large} label="Loading relationships..." />
            </Stack>
        );
    }
    
    return (
        <div className={styles.container}>
            {/* Header */}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.header}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <FlowRegular style={{ fontSize: '24px', color: '#0078d4' }} />
                    <Text variant="xLarge">Table Relationships</Text>
                </Stack>
                {hasChanges && (
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
                        You have unsaved changes
                    </MessageBar>
                )}
            </Stack>
            
            <CommandBar items={commandBarItems} farItems={commandBarFarItems} />
            
            <Separator />
            
            {/* Messages */}
            {error && (
                <MessageBar 
                    messageBarType={MessageBarType.error} 
                    onDismiss={() => setError(null)}
                    styles={{ root: { marginBottom: '12px' } }}
                >
                    {error}
                </MessageBar>
            )}
            
            {success && (
                <MessageBar 
                    messageBarType={MessageBarType.success}
                    onDismiss={() => setSuccess(null)}
                    styles={{ root: { marginBottom: '12px' } }}
                >
                    {success}
                </MessageBar>
            )}
            
            {/* Info about relationships */}
            <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginBottom: '16px' } }}>
                Define how tables relate to each other for multi-table queries. 
                The AI will use these relationships to generate correct JOIN clauses.
            </MessageBar>
            
            {/* Tables info */}
            {tables.length < 2 ? (
                <MessageBar messageBarType={MessageBarType.warning}>
                    At least 2 tables are required to define relationships. 
                    This source has {tables.length} table(s).
                </MessageBar>
            ) : (
                <Text variant="small" styles={{ root: { marginBottom: '16px' } }}>
                    Available tables: {tables.map(t => t.table_name).join(', ')}
                </Text>
            )}
            
            {/* Relationships List */}
            {relationships.length === 0 ? (
                <div className={styles.emptyState}>
                    <FlowRegular style={{ fontSize: '48px', color: '#0078d4' }} />
                    <Text variant="large" block styles={{ root: { marginTop: '16px' } }}>
                        No relationships defined
                    </Text>
                    <Text variant="medium" styles={{ root: { color: '#666' } }}>
                        Click "Add Relationship" to connect your tables
                    </Text>
                </div>
            ) : (
                <Stack tokens={{ childrenGap: 12 }}>
                    {relationships.map(rel => (
                        <div 
                            key={rel.id} 
                            className={`${styles.relationshipCard} ${rel.is_approved ? styles.relationshipCardApproved : ''}`}
                        >
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                                {/* Relationship Visualization */}
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                    <div className={styles.tableBox}>
                                        <Text className={styles.tableName}>
                                            {getTableDisplayName(rel.source_table_id)}
                                        </Text>
                                        <Text className={styles.columnName}>.{rel.source_column}</Text>
                                    </div>
                                    
                                    <Stack horizontalAlign="center">
                                        <Text className={styles.flowIndicator}>→</Text>
                                        <Text variant="tiny" styles={{ root: { color: '#666' } }}>
                                            {rel.join_type.toUpperCase()}
                                        </Text>
                                    </Stack>
                                    
                                    <div className={styles.tableBox}>
                                        <Text className={styles.tableName}>
                                            {getTableDisplayName(rel.target_table_id)}
                                        </Text>
                                        <Text className={styles.columnName}>.{rel.target_column}</Text>
                                    </div>
                                </Stack>
                                
                                {/* Actions */}
                                <Stack horizontal tokens={{ childrenGap: 8 }}>
                                    <IconButton
                                        iconProps={{ 
                                            iconName: rel.is_approved ? 'CheckMark' : 'CircleRing'
                                        }}
                                        title={rel.is_approved ? 'Approved - Click to unapprove' : 'Click to approve'}
                                        styles={{
                                            root: {
                                                color: rel.is_approved ? '#107c10' : '#666'
                                            }
                                        }}
                                        onClick={() => handleToggleApproval(rel.id)}
                                    />
                                    <IconButton
                                        iconProps={{ iconName: 'Delete' }}
                                        title="Delete relationship"
                                        onClick={() => handleDeleteRelationship(rel.id)}
                                    />
                                </Stack>
                            </Stack>
                            
                            {/* Details */}
                            <Stack horizontal tokens={{ childrenGap: 24 }} styles={{ root: { marginTop: '12px' } }}>
                                <TextField
                                    label="Relationship Name"
                                    placeholder="e.g., Order has Customer"
                                    value={rel.relationship_name}
                                    onChange={(_, v) => updateRelationship(rel.id, 'relationship_name', v || '')}
                                    styles={{ root: { minWidth: '200px' } }}
                                />
                                
                                <Dropdown
                                    label="Type"
                                    selectedKey={rel.relationship_type}
                                    options={relationshipTypeOptions}
                                    onChange={(_, opt) => updateRelationship(rel.id, 'relationship_type', opt?.key)}
                                    styles={{ root: { minWidth: '150px' } }}
                                />
                                
                                <Dropdown
                                    label="Join Type"
                                    selectedKey={rel.join_type}
                                    options={joinTypeOptions}
                                    onChange={(_, opt) => updateRelationship(rel.id, 'join_type', opt?.key)}
                                    styles={{ root: { minWidth: '150px' } }}
                                />
                            </Stack>
                            
                            {/* Auto-detected badge */}
                            {rel.auto_detected && (
                                <Text variant="small" styles={{ root: { marginTop: '8px', color: '#666', fontStyle: 'italic' } }}>
                                    Auto-detected (confidence: {(rel.confidence * 100).toFixed(0)}%)
                                </Text>
                            )}
                        </div>
                    ))}
                </Stack>
            )}
            
            {/* Add Relationship Dialog */}
            <Dialog
                hidden={!showAddDialog}
                onDismiss={() => setShowAddDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Add Table Relationship',
                    subText: 'Define how two tables relate to each other'
                }}
                modalProps={{
                    isBlocking: false,
                    styles: { main: { minWidth: '500px' } }
                }}
            >
                <Stack tokens={stackTokens}>
                    <Dropdown
                        label="Source Table"
                        required
                        placeholder="Select source table"
                        options={tableOptions}
                        selectedKey={newRelationship.source_table_id}
                        onChange={(_, opt) => {
                            const tableId = opt?.key as string;
                            setNewRelationship({
                                ...newRelationship,
                                source_table_id: tableId,
                                source_column: undefined // Reset column when table changes
                            });
                            if (tableId) fetchTableColumns(tableId);
                        }}
                    />
                    
                    <Dropdown
                        label="Source Column"
                        required
                        placeholder="Select column"
                        options={getTableColumns(newRelationship.source_table_id || '')}
                        selectedKey={newRelationship.source_column}
                        disabled={!newRelationship.source_table_id}
                        onChange={(_, opt) => setNewRelationship({
                            ...newRelationship,
                            source_column: opt?.key as string
                        })}
                    />
                    
                    <Separator />
                    
                    <Dropdown
                        label="Target Table"
                        required
                        placeholder="Select target table"
                        options={tableOptions.filter(t => t.key !== newRelationship.source_table_id)}
                        selectedKey={newRelationship.target_table_id}
                        onChange={(_, opt) => {
                            const tableId = opt?.key as string;
                            setNewRelationship({
                                ...newRelationship,
                                target_table_id: tableId,
                                target_column: undefined
                            });
                            if (tableId) fetchTableColumns(tableId);
                        }}
                    />
                    
                    <Dropdown
                        label="Target Column"
                        required
                        placeholder="Select column"
                        options={getTableColumns(newRelationship.target_table_id || '')}
                        selectedKey={newRelationship.target_column}
                        disabled={!newRelationship.target_table_id}
                        onChange={(_, opt) => setNewRelationship({
                            ...newRelationship,
                            target_column: opt?.key as string
                        })}
                    />
                    
                    <Separator />
                    
                    <Dropdown
                        label="Relationship Type"
                        options={relationshipTypeOptions}
                        selectedKey={newRelationship.relationship_type}
                        onChange={(_, opt) => setNewRelationship({
                            ...newRelationship,
                            relationship_type: opt?.key as string
                        })}
                    />
                    
                    <Dropdown
                        label="Join Type"
                        options={joinTypeOptions}
                        selectedKey={newRelationship.join_type}
                        onChange={(_, opt) => setNewRelationship({
                            ...newRelationship,
                            join_type: opt?.key as string
                        })}
                    />
                    
                    <TextField
                        label="Relationship Name"
                        placeholder="e.g., Order belongs to Customer"
                        value={newRelationship.relationship_name || ''}
                        onChange={(_, v) => setNewRelationship({
                            ...newRelationship,
                            relationship_name: v || ''
                        })}
                    />
                </Stack>
                
                <DialogFooter>
                    <PrimaryButton onClick={handleAddRelationship} text="Add" />
                    <DefaultButton onClick={() => setShowAddDialog(false)} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default RelationshipsEditor;
